import { useTranslation } from "react-i18next"

import type { HostedAgentSession, HostedAgentSummary } from "@/api/client/hosted-agents"

import { LockKeyhole, Users } from "@/components/icons"
import { cn } from "@/lib/utils"

import { agentWorkspaceVisibility } from "../utils/agent-workspace-utils"

type Props = {
  agent: HostedAgentSummary
  sessionId: string | null
  session?: HostedAgentSession
  className?: string
}

/**
 * Who can read the open thread (B6), as a header pill.
 *
 * Presentation only: the visibility it renders is still the session's
 * STAMPED `visibility` via `agentWorkspaceVisibility`, and the
 * `data-testid`/`data-visibility` contract the workspace e2e asserts on is
 * unchanged. The short pill label names the audience; the full sentence
 * stays as the accessible title, so nothing that was readable before is
 * lost.
 */
export function AgentAudience({ agent, sessionId, session, className }: Props) {
  const { t } = useTranslation()
  const visibility = agentWorkspaceVisibility(agent, sessionId, session)
  const Icon = visibility === "open" ? Users : LockKeyhole
  const readOnly = session?.currentUserRole === "reader"
  return (
    <span
      data-testid="agent-audience"
      data-visibility={visibility}
      title={t(`agents.audience.${visibility}`)}
      className={cn(
        "border-border bg-card text-muted-foreground inline-flex h-7 max-w-52 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium",
        className
      )}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="truncate">
        {t(`agents.audience.pill.${visibility}`)}
        {readOnly ? ` · ${t("agents.audience.read_only")}` : ""}
      </span>
    </span>
  )
}
